import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

/**
 * Dedicated OAuth callback page.
 * Handles both:
 *   - PKCE flow: ?code=...
 *   - Implicit flow: #access_token=...
 *   - Native app redirect: /auth/callback/native (or ?native=true) -> deep-links to easyfinder://
 * Then redirects to /dashboard or /onboarding.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [redirectingToApp, setRedirectingToApp] = useState(false);
  
  // Prevent double execution in StrictMode
  const didRun = useRef(false);

  useEffect(() => {
    // Only run once
    if (didRun.current) return;
    didRun.current = true;
    
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    // Safety timeout: if callback takes more than 10s, redirect to auth
    timeoutId = setTimeout(() => {
      if (!cancelled) {
        console.warn("[AuthCallback] Timeout reached");
        setError("Délai dépassé. Réessaie.");
        setTimeout(() => navigate("/auth", { replace: true }), 2000);
      }
    }, 10000);

    const handleCallback = async () => {
      try {
        // Check if this is a native app callback
        const isNativeCallback =
          searchParams.get("native") === "true" ||
          location.pathname.endsWith("/auth/callback/native");

        const code = searchParams.get("code");
        const errorParam = searchParams.get("error");
        const errorDescription = searchParams.get("error_description") || "";

        // Tokens can arrive either in query (deep links) or in hash (implicit flow)
        const queryAccessToken = searchParams.get("access_token");
        const queryRefreshToken = searchParams.get("refresh_token");

        // Also check for hash fragment (implicit flow / some OAuth responses)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hashAccessToken = hashParams.get("access_token");
        const hashRefreshToken = hashParams.get("refresh_token");
        const hashError = hashParams.get("error");
        const hashErrorDesc = hashParams.get("error_description") || "";

        const accessToken = queryAccessToken || hashAccessToken;
        const refreshToken = queryRefreshToken || hashRefreshToken;

        console.log("[AuthCallback] Params:", {
          native: isNativeCallback,
          hasCode: !!code,
          hasAccessToken: !!accessToken,
          hashError: hashError,
          error: errorParam,
        });

        // If this is a native app callback, redirect to custom URL scheme
        if (isNativeCallback) {
          const params = new URLSearchParams();
          
          if (code) params.set("code", code);
          if (accessToken) params.set("access_token", accessToken);
          if (refreshToken) params.set("refresh_token", refreshToken);
          if (errorParam || hashError) {
            params.set("error", errorParam || hashError || "");
            params.set("error_description", errorDescription || hashErrorDesc);
          }

          if (!code && !accessToken && !errorParam && !hashError) {
            console.log("[AuthCallback] Native callback without auth data");
            setError("Authentification incomplète. Réessaie.");
            setTimeout(() => {
              window.location.href = "easyfinder://auth";
            }, 2000);
            return;
          }

          setRedirectingToApp(true);
          const deepLinkUrl = `easyfinder://auth/callback?${params.toString()}`;
          console.log("[AuthCallback] Redirecting to native app:", deepLinkUrl);
          
          setTimeout(() => {
            window.location.href = deepLinkUrl;
          }, 500);
          return;
        }

        // Handle OAuth error (web flow)
        const finalError = errorParam || hashError;
        const finalErrorDesc = errorDescription || hashErrorDesc;
        
        if (finalError) {
          const desc = decodeURIComponent(finalErrorDesc);
          const lower = desc.toLowerCase();

          console.error("OAuth callback error:", { error: finalError, description: desc });

          if (lower.includes("invalid_client")) {
            setError("Connexion Google impossible : configuration Google invalide.");
          } else if (lower.includes("external code") || lower.includes("unable to exchange")) {
            setError("Connexion Google impossible : impossible d'échanger le code.");
          } else {
            setError(desc || "Erreur d'authentification Google");
          }

          setTimeout(() => navigate("/auth", { replace: true }), 2500);
          return;
        }

        // If we have access_token from implicit flow or deep link, set the session directly
        if (accessToken) {
          console.log("[AuthCallback] Setting session from access token");
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || "",
          });
          if (setSessionError) {
            console.error("Set session error:", setSessionError);
            setError("Impossible de finaliser la connexion. Réessaie.");
            setTimeout(() => navigate("/auth", { replace: true }), 2000);
            return;
          }
        }

        // Exchange code for session (PKCE flow)
        if (code) {
          console.log("[AuthCallback] Exchanging code for session");
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            console.error("Code exchange error:", exchangeError);
            setError("Impossible de finaliser la connexion. Réessaie.");
            setTimeout(() => navigate("/auth", { replace: true }), 2000);
            return;
          }
        }

        // At this point, Supabase client should have detected the session
        const { data: { session } } = await supabase.auth.getSession();

        if (cancelled) return;

        if (!session) {
          setError("Session non trouvée. Réessaie.");
          setTimeout(() => navigate("/auth", { replace: true }), 2000);
          return;
        }

        // Check onboarding status
        const { data: profile } = await supabase
          .from("profiles")
          .select("onboarding_completed")
          .eq("id", session.user.id)
          .single();

        if (cancelled) return;

        if (profile?.onboarding_completed) {
          navigate("/documents", { replace: true });
        } else {
          navigate("/onboarding", { replace: true });
        }
      } catch (err) {
        console.error("AuthCallback error:", err);
        if (!cancelled) {
          setError("Une erreur est survenue. Réessaie.");
          setTimeout(() => navigate("/auth", { replace: true }), 2000);
        }
      }
    };

    handleCallback();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [navigate, searchParams, location.pathname]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center space-y-4">
        {error ? (
          <>
            <p className="text-destructive font-medium">{error}</p>
            <p className="text-sm text-muted-foreground">Redirection vers la page de connexion...</p>
          </>
        ) : redirectingToApp ? (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            <p className="text-muted-foreground">Ouverture de l'application...</p>
            <p className="text-sm text-muted-foreground">Si l'app ne s'ouvre pas, retourne manuellement dans EasyFinder.</p>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            <p className="text-muted-foreground">Connexion en cours...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
